import { useState, useEffect } from "react";
import { Layout, Menu } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CommonImages,
  SideNavLatest,
  PartnerSideNavImages,
} from "../../../../reusableUI/images";
import secureLocalStorage from "react-secure-storage";
import { getSupport } from "../../../../Service/getSupport";
import "./sideBar.css";
import { readPartnerVerifiedFlag } from "../visitModels/authFlags";

const { Sider, Header } = Layout;

const readRole = () => {
  const local = localStorage.getItem("role");
  if (local && String(local).trim()) return String(local).trim().toLowerCase();

  const secure = secureLocalStorage.getItem("role");
  if (secure && String(secure).trim()) return String(secure).trim().toLowerCase();

  return "";
};

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const [role, setRole] = useState(() => readRole());
  const [partnerVerified, setPartnerVerified] = useState(() => readPartnerVerifiedFlag());
  const [unRead, setUnread] = useState(0);

  // Sync role + partnerVerified
  useEffect(() => {
    const sync = () => {
      setRole(readRole());
      setPartnerVerified(readPartnerVerifiedFlag());
    };

    window.addEventListener("storage", sync);     // other tabs (localStorage only)
    window.addEventListener("authUpdate", sync);  // same tab
    sync();

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("authUpdate", sync);
    };
  }, []);

  // Redirect from "/" or "/admin/login" to role home
  useEffect(() => {
    if (!role) return;

    const roleRoutes = {
      "super user": "/dashboard",
      support: "/support",
      "relationship manager": "/booking",
      hospital: "/partner/mct",
      agency: "/partner/mct",
      company: "/partner/mct",
      individual: "/partner/mct",
    };

    const targetRoute = roleRoutes[role];
    if (!targetRoute) return;

    if (currentPath === "/admin/login" || currentPath === "/") {
      navigate(targetRoute, { replace: true });
    }
  }, [role, currentPath, navigate]);

  // Partner route guard
  useEffect(() => {
    const isPartner =
      role === "hospital" ||
      role === "agency" ||
      role === "company" ||
      role === "individual";

    if (!isPartner) return;

    const verifiedOnlyRoutes = [
      "/branches",
      "/partner/mct",
      "/pricing",
      "/manageduty",
      "/partnerVisits",
      "/payouts",
    ];

    const isOnVerifiedRoute = verifiedOnlyRoutes.some(
      (p) => currentPath === p || currentPath.startsWith(`${p}/`)
    );

    // IMPORTANT: read from storage directly to avoid any state timing/race
    const pvNow = readPartnerVerifiedFlag();

    if (pvNow === false && isOnVerifiedRoute) {
      navigate("/partnerProfile", { replace: true });
    }
  }, [role, currentPath, navigate]);

  // Unread support count (admin/support only)
  useEffect(() => {
    const shouldFetch =
      role === "super user" || role === "support" || role === "relationship manager";
    if (!shouldFetch) return;

    const fetchSupport = async () => {
      const res = await getSupport();
      if (res?.error === false) setUnread(getUnreadCount(res.data));
    };

    fetchSupport();
  }, [role]);

  const getUnreadCount = (data) => {
    return (data || []).reduce((total, item) => {
      const count = (item.responses || []).filter(
        (r) => r?.seen === false && r?.who === "user"
      ).length;
      return total + count;
    }, 0);
  };

  const toggle = () => setCollapsed(!collapsed);
  const profilePage = localStorage.getItem("profilePage");

  const getSelectedKey = () => {
    if (profilePage === "user" && currentPath.match(/^\/profile\/[A-Za-z0-9%20-]+$/))
      return "7";

    if (profilePage === "hospital" && currentPath === "/Hospital") return "11";
    if (profilePage === "userProfile" && currentPath === "/hospitalProfile") return "7";
    if (profilePage === "partnerSide" && currentPath === "/partnerEdit") return "18";
    if (profilePage !== "userProfile" && currentPath === "/hospitalProfile") return "11";
    if (profilePage === "hospital" && currentPath === "/hospitalProfile") return "11";

    if (profilePage === "support" && currentPath.match(/^\/profile\/[A-Za-z0-9%20-]+$/))
      return "9";

    if (currentPath.startsWith("/booking")) return "6";
    if (currentPath === "/partnerEdit") return "11";

    if (
      currentPath.startsWith("/user") &&
      currentPath !== "/userAque" &&
      currentPath.includes("/profile")
    )
      return "7";

    switch (currentPath) {
      case "/dashboard": return "1";
      case "/userAque": return "2";
      case "/payment": return "5";
      case "/booking": return "6";
      case "/user": return "7";
      case "/notification": return "8";
      case "/support": return "9";
      case "/visits": return "10";
      case "/partnerlist":
      case "/addhospital":
      case "/hospitalProfile":
      case "/partnerEdit": return "11";
      case "/leads": return "12";
      case "/enquirylist": return "13";
      case "/access": return "14";
      case "/blockedNumbers": return "15";
      case "/account": return "16";

      // PARTNER
      case "/partnerProfile": return "31";
      case "/branches": return "32";
      case "/partner/mct":
      case "/staffsheduling": return "33";
      case "/pricing": return "34";
      case "/manageduty": return "35";
      case "/partnerVisits": return "36";
      case "/patientBooking": return "37";
      case "/payouts": return "38";
      case "/helpadmin": return "39";
      default: return "1";
    }
  };

  const menuItems = [
    {
      key: "1",
      label: "Dashboard",
      path: "/dashboard",
      icon: SideNavLatest.dashboardSelect,
      iconInactive: SideNavLatest.dashboardUnselect,
      roles: ["super user"],
      size: "h-6 w-4",
    },
    {
      key: "2",
      label: "User Acquisition",
      path: "/userAque",
      icon: SideNavLatest.userAcquSelect,
      iconInactive: SideNavLatest.userAcquUnselect,
      roles: ["super user"],
      size: "h-5 w-4",
    },
    {
      key: "5",
      label: "Payouts",
      path: "/payment",
      icon: SideNavLatest.paymentSelect,
      iconInactive: SideNavLatest.paymentUnselect,
      roles: ["super user"],
      size: "h-6 w-4",
    },
    {
      key: "6",
      label: "Bookings",
      path: "/booking",
      icon: SideNavLatest.bookingSelect,
      iconInactive: SideNavLatest.bookingUnselect,
      roles: ["super user", "relationship manager"],
      size: "h-6 w-4",
    },
    {
      key: "7",
      label: "Users",
      path: "/user",
      matchPaths: ["/user", "/profile", "/hospitalProfile"],
      icon: SideNavLatest.usersSelected,
      iconInactive: SideNavLatest.usersUnselected,
      roles: ["super user", "relationship manager"],
      size: "h-6 w-5",
    },
    {
      key: "8",
      label: "Notification Center",
      path: "/notification",
      icon: SideNavLatest.notificationSelected,
      iconInactive: SideNavLatest.notificationUnselected,
      roles: ["super user", "relationship manager"],
      size: "h-6 w-4",
    },
    {
      key: "9",
      label: "Support",
      path: "/support",
      roles: ["support", "super user"],
      icon: SideNavLatest.supportSelect,
      iconInactive: SideNavLatest.supportUnselect,
      size: "h-6 w-4",
    },
    {
      key: "10",
      label: "Manage Visits",
      path: "/visits",
      icon: SideNavLatest.visitSelected,
      iconInactive: SideNavLatest.visitUnselected,
      roles: ["relationship manager", "super user", "support"],
      size: "h-6 w-4",
    },
    {
      key: "11",
      label: "Partners",
      path: "/partnerlist",
      matchPaths: ["/partnerlist", "/addhospital", "/hospitalProfile", "/partnerEdit"],
      icon: SideNavLatest.partnerSelected,
      iconInactive: SideNavLatest.partnerUnselected,
      roles: ["relationship manager", "super user"],
      size: "h-6 w-5",
    },
    {
      key: "12",
      label: "Leads",
      path: "/leads",
      icon: SideNavLatest.leadsSelected,
      iconInactive: SideNavLatest.leadsUnselected,
      roles: ["super user"],
      size: "h-6 w-4",
    },
    {
      key: "13",
      label: "Enquiry List",
      path: "/enquirylist",
      icon: SideNavLatest.EnquirySelected,
      iconInactive: SideNavLatest.EnquiryUnselected,
      roles: ["super user"],
      size: "h-6 w-4",
    },
    {
      key: "14",
      label: "Manage Access",
      path: "/access",
      icon: SideNavLatest.manageSelected,
      iconInactive: SideNavLatest.manageUnselected,
      roles: ["super user"],
      size: "h-6 w-4",
    },
    {
      key: "15",
      label: "Blocked Users",
      path: "/blockedNumbers",
      icon: SideNavLatest.blockedSelected,
      iconInactive: SideNavLatest.blockedUnselected,
      roles: ["super user"],
      size: "h-6 w-4",
    },
    {
      key: "16",
      label: "Account",
      path: "/account",
      icon: SideNavLatest.accountSelected,
      iconInactive: SideNavLatest.accountUnselected,
      roles: ["relationship manager", "super user", "support"],
      size: "h-6 w-4",
    },
    ...(role === "hospital" || role === "agency" || role === "company" || role === "individual"
      ? [
        {
          key: "31",
          label: "Partner Profile",
          path: "/partnerProfile",
          icon: SideNavLatest.accountSelected,
          iconInactive: SideNavLatest.accountUnselected,
          roles: ["hospital", "agency", "company", "individual"],
          size: "h-5 w-4",
        },

        ...(partnerVerified === true
          ? [
            {
              key: "32",
              label: "Branches",
              path: "/branches",
              icon: PartnerSideNavImages.branch_s,
              iconInactive: PartnerSideNavImages.branch_u,
              roles: ["hospital", "agency", "company", "individual"],
              size: "h-5 w-4",
            },
            {
              key: "33",
              label: "Care Team",
              path: "/partner/mct",
              matchPaths: ["/partner/mct", "/staffsheduling"],
              icon: SideNavLatest.partnerSelected,
              iconInactive: SideNavLatest.partnerUnselected,
              roles: ["hospital", "agency", "company"],
              size: "h-6 w-4",
            },
            {
              key: "37",
              label: "Patients",
              path: "/patientBooking",
              icon: SideNavLatest.usersSelected,
              iconInactive: SideNavLatest.usersUnselected,
              roles: ["hospital"],
              size: "h-5 w-4",
            },
            {
              key: "34",
              label: "Services & Fees",
              path: "/pricing",
              icon: PartnerSideNavImages.service_s,
              iconInactive: PartnerSideNavImages.service_u,
              roles: ["hospital", "agency", "company", "individual"],
              size: "h-5 w-4",
            },
            {
              key: "35",
              label: "Bookings",
              path: "/manageduty",
              icon: PartnerSideNavImages.billing_s,
              iconInactive: PartnerSideNavImages.billing_u,
              roles: ["hospital", "agency", "company"],
              size: "h-5 w-4",
            },
            {
              key: "36",
              label: "Visits",
              path: "/partnerVisits",
              icon: PartnerSideNavImages.visit_s,
              iconInactive: PartnerSideNavImages.visit_u,
              roles: ["hospital", "agency", "company", "individual"],
              size: "h-5 w-4",
            },

            {
              key: "38",
              label: "Payments",
              path: "/payouts",
              icon: PartnerSideNavImages.payment_s,
              iconInactive: PartnerSideNavImages.payment_u,
              roles: ["hospital", "agency", "company", "individual"],
              size: "h-5 w-4",
            },
          ]
          : []),

        {
          key: "39",
          label: "Support",
          path: "/helpadmin",
          icon: PartnerSideNavImages.support_s,
          iconInactive: PartnerSideNavImages.support_u,
          roles: ["hospital", "agency", "company", "individual"],
          size: "h-5 w-4",
        },
      ]
      : []),
  ];

  const filteredMenuItems = menuItems.filter((item) => item.roles.includes(role));
  if (!role) return null;

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      className="bg-white shadow-lg h-[96%] rounded-[12px] p-1"
    >
      <Header className={`bg-white ${collapsed ? "p-6" : "p-2"}`}>
        <div onClick={toggle} className="cursor-pointer">
          <img src={CommonImages.NavIcon} className="h-6 w-5" alt="navigation icon" />
        </div>
      </Header>

      {!collapsed && (
        <motion.div className="flex flex-col items-center bg-white">
          <div className="flex items-center mb-5 mt-[-55px]">
            <img src={CommonImages.CevIcon} className="h-[26px] w-[100px]" alt="Logo" />
          </div>
          <img
            src={CommonImages.CommonDp}
            className="h-12 w-12 rounded-full object-cover"
            alt="Profile"
          />
        </motion.div>
      )}

      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        className="flex flex-col items-center justify-start gap-1 border-none bg-white w-full pt-4"
        style={{ height: "75vh", borderRight: "none" }}
      >
        {filteredMenuItems.map((item) => {
          const isMatched = item.matchPaths
            ? item.matchPaths.some(
              (path) => currentPath === path || currentPath.startsWith(`${path}/`)
            )
            : currentPath === item.path;

          return (
            <Menu.Item
              key={item.key}
              icon={
                item.key === "9" ? (
                  <span className="supportIconWrap">
                    <img
                      src={isMatched ? item.icon : item.iconInactive}
                      className={`${item.size} object-contain`}
                      alt={item.label}
                    />
                    {Number(unRead) > 0 && (
                      <span className="supportUnreadBadge">
                        {unRead > 9 ? "9+" : unRead}
                      </span>
                    )}
                  </span>
                ) : (
                  <img
                    src={isMatched ? item.icon : item.iconInactive}
                    className={`${item.size} object-contain`}
                    alt={item.label}
                  />
                )
              }
              className="mt-2"
            >
              <Link to={item.path} className="text-[13px]">
                {item.label}
              </Link>
            </Menu.Item>
          );
        })}
      </Menu>
    </Sider>
  );
};

export default Sidebar;